import {
  Component,
  DoCheck,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { EMPTY, Subscription, of, throwError } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { WindowCloseResult } from '@progress/kendo-angular-dialog';

import { ModalType } from '../core/models/componentContract.model';
import {
  AuthMode,
  PopupButtonController,
  PopupConfig,
  Resource,
  WizardConfig,
} from '../core/models/dataContract.model';
import { TransService } from '../core/models/translation.model';

import { ConfigService } from '../core/services/config.service';
import { ModalService } from '../core/services/modal.service';
import { ResourceService } from '../core/services/resource.service';
import { ClipboardService, IClipboardResponse } from 'ngx-clipboard';
import { UtilsService } from '../core/services/utils.service';

import { PopupWizardComponent } from '../core/components/popup-wizard/popup-wizard.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalComponent } from '../core/components/modal/modal.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { IdentityEditorConfig } from '../core/models/editorContract.model';
import { NgForm } from '@angular/forms';
import { AuthService } from '../core/services/auth.service';
import { HelperService } from '../core/services/helper.service';

@Component({
  selector: 'app-wizard-setting',
  templateUrl: './wizard-setting.component.html',
  styleUrls: ['./wizard-setting.component.scss'],
  standalone: false
})
export class WizardSettingComponent implements OnInit, OnDestroy, DoCheck {
  private subscription = new Subscription();

  private tape = [undefined, true, false];

  @ViewChild('popupWizard')
  popupWizard: PopupWizardComponent;

  @ViewChild('workflowTemplateForm')
  workflowTemplateForm: NgForm;

  wizardTypes = ['dialog', 'stepper'];

  type: string;

  idpConfig: IdentityEditorConfig;
  idpWorkflowConfig: IdentityEditorConfig;

  idpWorkflow = {
    dataType: 'Reference',
    displayName: 'key_workflowOrTemplate',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'idpWorkflow',
    value: null,
    values: [],
  };

  templateResource = {
    dataType: 'Reference',
    displayName: 'key_workflowTemplate',
    multivalued: false,
    permissionHint: 'Add, Create, Modify, Delete, Read, Remove',
    systemName: 'TemplateResource',
    value: null,
    values: [],
  };

  showWizard: boolean;
  availableWizards: Array<WizardConfig> = [];
  selectedWizard: WizardConfig;
  selectedWizardBackup: WizardConfig | null = null;
  parameterConfig: Array<any> = [];

  saveButtonDisabled = false;
  wizardSaved = false;

  get hasAdminRight() {
    return this.resource.isAdminViewSet;
  }

  private clearWorkflowTemplate() {
    this.templateResource.value = null;
    this.templateResource.values = [];
    this.selectedWizard.workflowTemplate = '';
    this.selectedWizard.workflowTemplateInput = {};
    this.parameterConfig = [];
  }

  private buildParameterConfig(conf: any) {
    if (conf) {
      for (const [key, value] of Object.entries(conf)) {
        value['paramKey'] = key;

        // value['paramValue'] = null;
        value['paramValue'] =
          this.selectedWizard.workflowTemplateInput &&
            this.selectedWizard.workflowTemplateInput[key]
            ? this.selectedWizard.workflowTemplateInput[key]
            : null;

        this.parameterConfig.push(value);
      }

      this.buildValue();

      setTimeout(() => {
        if (this.workflowTemplateForm && this.workflowTemplateForm.controls) {
          Object.keys(this.workflowTemplateForm.controls).forEach((key) => {
            if (key.startsWith('parameterValue')) {
              this.workflowTemplateForm.controls[key].markAsTouched();
            }
          });
        }
      }, 200);
    }
  }

  private buildValue() {
    const resultValue = {};
    this.parameterConfig.forEach((config) => {
      resultValue[config.paramKey] = config.paramValue;
    });
    this.selectedWizard.workflowTemplateInput = resultValue;
  }

  private getTemplateInfo(id: string) {
    this.subscription.add(
      this.resource
        .getResourceByID(id, ['displayname', 'inputparameters'])
        .pipe(
          tap((result: Resource) => {
            this.buildParameterConfig(
              this.utils.ExtraValue(result, 'inputparameters')
            );
          })
        )
        .subscribe()
    );
  }

  private restoreWizardSetting(selectedWizard: WizardConfig) {
    if (!this.wizardSaved) {
      if (
        this.selectedWizardBackup &&
        this.selectedWizardBackup.name &&
        selectedWizard &&
        selectedWizard.name !== this.selectedWizardBackup.name
      ) {
        const pos = this.availableWizards.findIndex(
          (w) => w.name === this.selectedWizardBackup.name
        );
        if (pos >= 0) {
          this.availableWizards[pos] = this.utils.DeepCopy(
            this.selectedWizardBackup
          );

          if (
            this.resource.primaryViewSetting.creationView[
            this.selectedWizardBackup.name
            ]
          ) {
            this.resource.primaryViewSetting.creationView[
              this.selectedWizardBackup.name
            ] = this.utils.DeepCopy(this.selectedWizardBackup);
          }
        }
      }
    } else {
      this.wizardSaved = false;
    }

    if (selectedWizard && selectedWizard.name) {
      this.selectedWizardBackup = this.utils.DeepCopy(selectedWizard);
    }
  }

  constructor(
    private resource: ResourceService,
    private config: ConfigService,
    private translate: TransService,
    private modal: ModalService,
    private clipboard: ClipboardService,
    private snackbar: MatSnackBar,
    private utils: UtilsService,
    private route: ActivatedRoute,
    private auth: AuthService,
    private helper: HelperService
  ) { }

  ngOnInit(): void {
    this.type = this.route.snapshot.paramMap.get('type');

    this.initWizards();

    this.subscription.add(
      this.clipboard.copyResponse$.subscribe((res: IClipboardResponse) => {
        if (res.isSuccess) {
          this.snackbar.open(this.translate.instant('key_textCopied'), 'OK', {
            duration: 2000,
          });
        }
      })
    );

    const tagName = this.config.getConfig('executableWorkflowTag', '');
    this.idpWorkflowConfig = {
      required: true,
      isMultivalue: false,
      lettersToTrigger: this.config.getConfig('lettersToTriggerSearch', 3),
      queryNormalSearch: tagName
        ? `/workflowtemplate[tags='${tagName}' and starts-with(DisplayName,'%SearchText%')]`
        : `/workflowtemplate[starts-with(DisplayName,'%SearchText%')]`,
      allowEmptySearch: true,
      queryEmptySearch: tagName
        ? `/workflowtemplate[tags='${tagName}']`
        : `/workflowtemplate`,
      attributesToShow: [
        {
          field: 'DisplayName',
          sortable: true,
          filterable: true,
          filter: 'text',
        },
        {
          field: 'ObjectType',
          sortable: true,
        },
      ],
    };
    this.subscription.add(
      this.resource
        .isUISettingsUpToDate()
        .pipe(
          tap((result: string) => {
            if (result === 'expired') {
              this.modal.show(
                ModalType.error,
                'key_warning',
                'key_uiRefreshNeeded'
              );
            }
          })
        )
        .subscribe()
    );
  }

  ngDoCheck() {
    let workflowTemplateFormValid = true;
    if (this.workflowTemplateForm) {
      workflowTemplateFormValid = this.workflowTemplateForm.valid;
    }

    this.saveButtonDisabled = !workflowTemplateFormValid;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get isCloud(): boolean {
    return this.auth.authMode === AuthMode.azure;
  }

  initWizards() {
    const viewSetting = this.resource.primaryViewSetting.creationView;
    if (viewSetting) {
      this.availableWizards.splice(0, this.availableWizards.length);
      Object.keys(viewSetting).forEach((setting) => {
        const item: WizardConfig = viewSetting[setting];
        item.name = setting;
        if (this.type === 'creationform' && !item.isCreation) {
          return;
        }
        if (this.type === 'wizard' && item.isCreation) {
          return;
        }
        if (item.isCreation === undefined) {
          item.isCreation = true;
        }
        if (item.maxHeight === undefined) {
          item.maxHeight = 600;
        }
        if (item.canSubmitAtStep === undefined) {
          item.canSubmitAtStep = 0;
        }
        if (item.width === undefined) {
          item.width = 800;
        }
        if (item.displayName === undefined) {
          item.displayName = 'key_creationWizard';
        }
        this.availableWizards.push(item);
      });
      this.availableWizards.sort((a, b) => {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        } else {
          return -1;
        }
      });
    } else {
      this.availableWizards = [];
    }
  }

  onWizardSaved() {
    this.wizardSaved = true;
  }

  onWizardSelectionChange(restoreSetting = true) {
    if (restoreSetting) {
      this.restoreWizardSetting(this.selectedWizard);
    }

    if (this.selectedWizard) {
      this.showWizard = true;

      const prg = this.modal.show(
        ModalType.progress,
        'key_processing',
        '',
        '300px'
      );

      this.subscription.add(
        this.resource
          .getType(
            this.selectedWizard.objectType,
            this.config.getCulture(this.translate.currentCulture)
          )
          .pipe(
            tap((schema) => {
              if (schema && this.popupWizard) {
                this.popupWizard.initWizard(this.selectedWizard, schema);
              }
            }),
            finalize(() => {
              if (prg) {
                prg.close();
              }
            })
          )
          .subscribe()
      );
      if (this.selectedWizard.executeWorkflowTemplate) {
        if (this.selectedWizard.workflowTemplate) {
          this.templateResource.value = this.selectedWizard.workflowTemplate;
          this.templateResource.values = [this.selectedWizard.workflowTemplate];
          this.onWorkflowChange();
        } else {
          this.clearWorkflowTemplate();
        }
      }
    } else {
      this.showWizard = false;
      this.popupWizard.initWizard();
    }
  }

  onAddWizard() {
    let process: MatDialogRef<ModalComponent> = null;

    const popupConfig: PopupConfig = new PopupConfig();
    popupConfig.title =
      this.type === 'creationform' ? 'l10n_creationForm' : 'l10n_wizard';
    popupConfig.style = 'outline';
    popupConfig.width = 620;
    // dictionary key order is the display order, javascript orders the keys automatically
    popupConfig.data =
      this.type === 'creationform'
        ? {
          a_isNavigation: {
            text: 'key_navigateToResource',
            value: false,
            type: 'boolean',
            width: 50,
          },
          a_name: {
            text: 'key_name',
            value: '',
            type: 'text',
            width: 50,
            validation: `^[a-z0-9_.-]*$`,
            required: true,
            focused: true,
          },
          a_nameDisplay: {
            text: 'key_displayName',
            value: '',
            type: 'text',
            width: 50,
            required: true,
          },
          a_resourceType: {
            text: 'key_resourceType',
            value: '',
            type: 'text',
            width: 50,
            validation: `^[a-zA-Z0-9_.-]*$`,
            required: true,
          },
          a_type: {
            text: 'key_type',
            value: 'dialog',
            options: this.wizardTypes,
            type: 'select',
            width: 50,
          },
          b_navigationKey: {
            text: 'key_navigationKey',
            value: '',
            type: 'text',
            width: 50,
            validation: `^[a-zA-Z0-9_.-]*$`,
          },
          c_icon: {
            text: 'key_stateCardIconText',
            value: '',
            type: 'text',
            width: 50,
            validation: `^[a-z0-9_.-]*$`,
          },
          c_width: {
            text: 'key_width',
            value: 800,
            type: 'number',
            width: 50,
            required: true,
          },
          d_saveStep: {
            text: 'key_canSubmitAtStep',
            value: 0,
            type: 'number',
            width: 50,
            required: false,
          },
        }
        : {
          a_name: {
            text: 'key_name',
            value: '',
            type: 'text',
            width: 50,
            validation: `^[a-z0-9_.-]*$`,
            required: true,
            focused: true,
          },
          a_nameDisplay: {
            text: 'key_displayName',
            value: '',
            type: 'text',
            width: 50,
            required: true,
          },
          a_type: {
            text: 'key_type',
            value: 'dialog',
            options: this.wizardTypes,
            type: 'select',
            width: 50,
          },
          c_icon: {
            text: 'key_stateCardIconText',
            value: '',
            type: 'text',
            width: 50,
            validation: `^[a-z0-9_.-]*$`,
          },
          c_width: {
            text: 'key_width',
            value: 800,
            type: 'number',
            width: 50,
            required: true,
          },
          d_saveStep: {
            text: 'key_canSubmitAtStep',
            value: 0,
            type: 'number',
            width: 50,
            required: false,
          },
        };

    let popupResult: any;

    let keyword = '';
    try {
      keyword = `/${this.type}/add`;
    } catch { }
    this.subscription.add(
      of(keyword).pipe(
        switchMap((word: string) => {
          if (word) {
            return this.helper.isUISettingsEditable(word);
          } else {
            return of(true);
          }
        }),
        switchMap((isEditable: boolean) => {
          if (isEditable) {
            return this.modal.popup(popupConfig);
          } else {
            this.modal.show(
              ModalType.error,
              'key_error',
              'key_saveSettingsNotAllowed'
            );
            return EMPTY;
          }
        }),
        switchMap((windowResult: any) => {
          if (!(windowResult instanceof WindowCloseResult)) {
            popupResult = windowResult;

            const viewSetting = this.resource.primaryViewSetting.creationView;
            if (
              Object.keys(viewSetting).findIndex(
                (key) =>
                  key.toLowerCase() ===
                  popupResult.a_name.value.toLowerCase()
              ) >= 0
            ) {
              this.modal.show(
                ModalType.error,
                'key_error',
                'l10n_wizardAlreadyExists'
              );
              return EMPTY;
            }

            const wizardConfig = {
              name: popupResult.a_name.value.toLowerCase(),
              displayName: popupResult.a_nameDisplay.value,
              isCreation: this.type === 'creationform',
              navigateToResource: popupResult.a_isNavigation
                ? popupResult.a_isNavigation.value
                : false,
              objectType: popupResult.a_resourceType
                ? popupResult.a_resourceType.value
                : 'person',
              type: popupResult.a_type.value,
              navigationKey: popupResult.b_navigationKey
                ? popupResult.b_navigationKey.value
                : null,
              icon: popupResult.c_icon.value,
              width: popupResult.c_width.value,
              canSubmitAtStep: popupResult.d_saveStep.value,
              sections: [
                {
                  name: 'general',
                  displayName: 'key_general',
                  attributes: [],
                },
              ],
            };
            this.restoreWizardSetting(wizardConfig);
            viewSetting[popupResult.a_name.value.toLowerCase()] =
              wizardConfig;

            return this.helper.showPersistUISettingOptions();
          }
          return EMPTY;
        }),
        switchMap((windowResult: any) => {
          if (!(windowResult instanceof WindowCloseResult)) {
            process = this.modal.show(
              ModalType.progress,
              'key_savingChanges',
              '',
              '300px'
            );

            this.initWizards();
            this.selectedWizard = this.availableWizards.find(
              (w) =>
                w.name.toLowerCase() ===
                popupResult.a_name.value.toLowerCase()
            );
            this.onWizardSelectionChange(false);

            return this.helper.updateUISettings(windowResult);
          } else {
            return of('canceled');
          }
        }),
        tap((updateResult: any) => {
          if (updateResult === 'expired') {
            this.modal.show(
              ModalType.error,
              'key_warning',
              'key_uiRefreshNeeded'
            );
          } else if (updateResult === 'canceled') {
            try {
              delete this.resource.primaryViewSetting.creationView[popupResult.a_name.value.toLowerCase()];
            } catch { }
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.modal.show(ModalType.error, 'key_error', this.utils.getServiceError(err));
          try {
            delete this.resource.primaryViewSetting.creationView[popupResult.a_name.value.toLowerCase()];
          } catch { }
          return EMPTY;
        }),
        finalize(() => {
          if (process) {
            process.close();
          }
        })
      ).subscribe()
    );
  }

  onRemoveWizard() {
    let process: MatDialogRef<ModalComponent> = null;

    let keyword = '';
    try {
      keyword = `/${this.type}/delete`;
    } catch { }
    this.subscription.add(
      of(keyword).pipe(
        switchMap((word: string) => {
          if (word) {
            return this.helper.isUISettingsEditable(word);
          } else {
            return of(true);
          }
        }),
        switchMap((isEditable: boolean) => {
          if (isEditable) {
            return this.modal.show(
              ModalType.confirm,
              'key_confirmation',
              'key_confirmDeleteResource'
            ).afterClosed();
          } else {
            this.modal.show(
              ModalType.error,
              'key_error',
              'key_saveSettingsNotAllowed'
            );
            return EMPTY;
          }
        }),
        switchMap((result) => {
          if (result && result === 'yes') {
            return this.helper.showPersistUISettingOptions();
          }
          return EMPTY;
        }),
        switchMap((windowResult: any) => {
          if (!(windowResult instanceof WindowCloseResult)) {
            process = this.modal.show(
              ModalType.progress,
              'key_savingChanges',
              '',
              '300px'
            );

            const viewSetting = this.resource.primaryViewSetting.creationView;
            delete viewSetting[this.selectedWizard.name.toLowerCase()];

            return this.helper.updateUISettings(windowResult);
          } else {
            return of('canceled');
          }
        }),
        tap((updateResult: any) => {
          if (updateResult === 'expired') {
            this.modal.show(
              ModalType.error,
              'key_warning',
              'key_uiRefreshNeeded'
            );
          } else if (updateResult !== 'canceled') {
            this.showWizard = false;
            this.selectedWizard = null;
            this.initWizards();
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.modal.show(ModalType.error, 'key_error', this.utils.getServiceError(err));
          return EMPTY;
        }),
        finalize(() => {
          if (process) {
            process.close();
          }
        })
      ).subscribe()
    );
  }

  onExportWizard() {
    const popupConfig: PopupConfig = new PopupConfig();
    popupConfig.title = 'key_export';
    popupConfig.style = 'outline';
    popupConfig.singleButton = true;
    popupConfig.confirmButton = new PopupButtonController(
      'key_export',
      'primary'
    );
    popupConfig.data = {
      name: {
        text: 'key_name',
        value: this.selectedWizard.name.toLowerCase(),
        type: 'text',
        required: true,
        focused: true,
        validation: '^[a-z0-9]*$',
      },
    };

    this.subscription.add(
      this.modal
        .popup(popupConfig)
        .pipe(
          tap((windowResult: any) => {
            if (!(windowResult instanceof WindowCloseResult)) {
              const viewSetting = this.resource.primaryViewSetting.creationView;
              const objExport = {
                creationView: {},
              };
              objExport.creationView[windowResult.name.value] =
                viewSetting[this.selectedWizard.name.toLowerCase()];

              this.clipboard.copy(
                this.utils.stringifyComponentConfig(objExport)
              );
            }
          })
        )
        .subscribe()
    );
  }

  onImportWizard() {
    const popupConfig: PopupConfig = new PopupConfig();
    popupConfig.title = 'key_import';
    popupConfig.style = 'outline';
    popupConfig.data = {
      name: {
        text: 'key_name',
        value: undefined,
        type: 'text',
        required: false,
        focused: false,
        validation: '^[a-z0-9]*$',
      },
      settings: {
        text: 'key_settings',
        value: '',
        type: 'textarea',
        rows: 6,
        required: true,
        focused: true,
      },
    };

    let settingsToImport: any;
    let keyName = '';
    let customName = '';
    let process: MatDialogRef<ModalComponent, any>;


    let keyword = '';
    try {
      keyword = `/${this.type}/add`;
    } catch { }
    this.subscription.add(
      of(keyword).pipe(
        switchMap((word: string) => {
          if (word) {
            return this.helper.isUISettingsEditable(word);
          } else {
            return of(true);
          }
        }),
        switchMap((isEditable: boolean) => {
          if (isEditable) {
            return this.modal.popup(popupConfig);
          } else {
            this.modal.show(
              ModalType.error,
              'key_error',
              'key_saveSettingsNotAllowed'
            );
            return EMPTY;
          }
        }),
        switchMap((windowResult: any) => {
          if (!(windowResult instanceof WindowCloseResult)) {
            if (
              windowResult &&
              windowResult.settings &&
              windowResult.settings.value
            ) {
              settingsToImport = JSON.parse(windowResult.settings.value);
              if (settingsToImport.creationView) {
                if (Object.keys(settingsToImport.creationView).length === 1) {
                  keyName = Object.keys(settingsToImport.creationView)[0];
                  customName = windowResult.name.value ?? keyName;
                  const keyPos = Object.keys(
                    this.resource.primaryViewSetting.creationView
                  ).findIndex(
                    (key: string) =>
                      key.toLowerCase() === customName.toLowerCase()
                  );
                  if (keyPos >= 0) {
                    return this.modal
                      .show(
                        ModalType.confirm,
                        'key_confirmation',
                        `${this.translate.instant(
                          'key_confirmOverwriteFormSettingsFor'
                        )} ${customName}?`
                      )
                      .afterClosed();
                  } else {
                    return of('yes');
                  }
                }
              }

              return throwError({
                message: this.translate.instant('key_invalidSetting'),
              });
            } else {
              this.modal.show(
                ModalType.error,
                'key_error',
                'key_invalidSetting'
              );
            }
          }
          return EMPTY;
        }),
        switchMap((dialogResult: any) => {
          if (dialogResult && dialogResult === 'yes') {
            return this.helper.showPersistUISettingOptions();
          }
          return EMPTY;
        }),
        switchMap((windowResult: any) => {
          if (!(windowResult instanceof WindowCloseResult)) {
            process = this.modal.show(
              ModalType.progress,
              'key_savingChanges',
              '',
              '300px'
            );

            this.restoreWizardSetting(settingsToImport.creationView[keyName]);
            this.resource.primaryViewSetting.creationView[customName] =
              settingsToImport.creationView[keyName];

            return this.helper.updateUISettings(windowResult);
          } else {
            return of('canceled');
          }
        }),
        tap((updateResult: any) => {
          if (updateResult === 'expired') {
            this.modal.show(
              ModalType.error,
              'key_warning',
              'key_uiRefreshNeeded'
            );
          } else if (updateResult === 'canceled') {
            try {
              delete this.resource.primaryViewSetting.creationView[customName];
            } catch { }
          } else {
            this.initWizards();
            this.selectedWizard = this.availableWizards.find(
              (w) => w.name.toLowerCase() === customName.toLowerCase()
            );
            this.onWizardSelectionChange(false);
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.modal.show(ModalType.error, 'key_error', this.utils.getServiceError(err));
          return EMPTY;
        }),
        finalize(() => {
          if (process) {
            process.close();
          }
        })
      ).subscribe()
    );
  }

  onSetNavigateToResource() {
    const nextValue =
      this.tape[
      (this.tape.indexOf(this.selectedWizard.navigateToResource) + 1) %
      this.tape.length
      ];
    this.selectedWizard.navigateToResource = nextValue;
  }

  onWorkflowChange() {
    this.selectedWizard.workflowTemplate = this.utils.ExtraValue(
      this.templateResource,
      'value:ObjectID'
    );
    this.parameterConfig = [];
    if (this.selectedWizard.workflowTemplate) {
      this.getTemplateInfo(this.selectedWizard.workflowTemplate);
    } else {
      this.selectedWizard.workflowTemplateInput = {};
    }
  }

  onExecuteWorkflowTemplateChange() {
    if (!this.selectedWizard.executeWorkflowTemplate) {
      this.clearWorkflowTemplate();
    }
  }

  onValueChange() {
    this.buildValue();
  }
}
